import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../services/auth';

interface MedicalHistory {
  allergies: string[];
  conditions: string[];
  medications: string[];
  blood_group: string;
  bp: string;
  diabetes_status: string;
  pregnancy: string;
  smoking: boolean;
  alcohol: boolean;
  notes: string;
}

const EMPTY: MedicalHistory = {
  allergies: [], conditions: [], medications: [],
  blood_group: '', bp: '', diabetes_status: 'None',
  pregnancy: 'N/A', smoking: false, alcohol: false, notes: '',
};

const COMMON_CONDITIONS = [
  'Hypertension', 'Diabetes Type 1', 'Diabetes Type 2', 'Heart Disease',
  'Asthma', 'Thyroid Disorder', 'Kidney Disease', 'Liver Disease',
  'HIV/AIDS', 'Hepatitis B', 'Hepatitis C', 'Epilepsy', 'Blood Disorder',
  'Rheumatic Fever', 'Cancer', 'Tuberculosis',
];

const COMMON_ALLERGIES = [
  'Penicillin', 'Aspirin', 'Sulfa Drugs', 'Latex', 'Local Anesthesia',
  'Ibuprofen', 'Codeine', 'Metal/Nickel',
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

interface Props {
  patientId: string;
  onClose: () => void;
}

export default function MedicalHistoryForm({ patientId, onClose }: Props) {
  const [data, setData] = useState<MedicalHistory>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    axios.get(`/api/patients/${patientId}`, { headers })
      .then((r) => {
        const h = r.data?.medical_history;
        if (h && typeof h === 'object') setData({ ...EMPTY, ...h });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const toggleList = (field: 'allergies' | 'conditions' | 'medications', item: string) => {
    setData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item) ? prev[field].filter((x) => x !== item) : [...prev[field], item],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/patients/${patientId}`, { medical_history: data }, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 16, cursor: 'pointer',
    fontSize: 12, fontWeight: 500, margin: '2px 4px 2px 0', transition: 'all 0.2s',
    background: active ? 'rgba(220,38,38,0.12)' : 'rgba(0,0,0,0.04)',
    color: active ? '#dc2626' : '#64748b',
    border: `1px solid ${active ? 'rgba(220,38,38,0.3)' : 'rgba(0,0,0,0.08)'}`,
  });

  const sectionStyle: React.CSSProperties = { marginBottom: 16 };
  const labelStyle: React.CSSProperties = { fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 6, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 520, maxHeight: '85vh',
        overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>📋 Medical History</h3>

        <div style={sectionStyle}>
          <label style={labelStyle}>Allergies</label>
          <div>{COMMON_ALLERGIES.map((a) => (
            <span key={a} style={chipStyle(data.allergies.includes(a))} onClick={() => toggleList('allergies', a)}>{a}</span>
          ))}</div>
        </div>

        <div style={sectionStyle}>
          <label style={labelStyle}>Medical Conditions</label>
          <div>{COMMON_CONDITIONS.map((c) => (
            <span key={c} style={chipStyle(data.conditions.includes(c))} onClick={() => toggleList('conditions', c)}>{c}</span>
          ))}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...sectionStyle }}>
          <div>
            <label style={labelStyle}>Blood Group</label>
            <select value={data.blood_group} onChange={(e) => setData({ ...data, blood_group: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <option value="">Select</option>
              {BLOOD_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Blood Pressure</label>
            <input value={data.bp} onChange={(e) => setData({ ...data, bp: e.target.value })}
              placeholder="e.g. 120/80" style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...sectionStyle }}>
          <div>
            <label style={labelStyle}>Diabetes</label>
            <select value={data.diabetes_status} onChange={(e) => setData({ ...data, diabetes_status: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              {['None', 'Type 1', 'Type 2', 'Pre-diabetic', 'Gestational'].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Pregnancy</label>
            <select value={data.pregnancy} onChange={(e) => setData({ ...data, pregnancy: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              {['N/A', 'Not Pregnant', 'Pregnant', 'Lactating'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, ...sectionStyle }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={data.smoking} onChange={(e) => setData({ ...data, smoking: e.target.checked })} />
            <span style={{ fontSize: 13 }}>🚬 Smoking</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={data.alcohol} onChange={(e) => setData({ ...data, alcohol: e.target.checked })} />
            <span style={{ fontSize: 13 }}>🍺 Alcohol</span>
          </label>
        </div>

        <div style={sectionStyle}>
          <label style={labelStyle}>Current Medications</label>
          <input placeholder="Type medication and press Enter" style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter') { const v = (e.target as HTMLInputElement).value.trim(); if (v) { toggleList('medications', v); (e.target as HTMLInputElement).value = ''; } } }} />
          <div style={{ marginTop: 4 }}>{data.medications.map((m) => (
            <span key={m} style={{ ...chipStyle(true), background: 'rgba(59,130,246,0.1)', color: '#2563eb', borderColor: 'rgba(59,130,246,0.2)' }}
              onClick={() => toggleList('medications', m)}>{m} ✕</span>
          ))}</div>
        </div>

        <div style={sectionStyle}>
          <label style={labelStyle}>Additional Notes</label>
          <textarea value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })}
            rows={2} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
            background: saved ? '#059669' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)', color: '#fff',
          }}>{saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Medical History'}</button>
        </div>
      </div>
    </div>
  );
}
